import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Search, Plus, LogOut, User as UserIcon, LogIn, Sparkles, Shield, Store, Menu, MapPin, Coins } from "lucide-react";import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import NotificationBell from "@/components/NotificationBell";
import MessagesBell from "@/components/MessagesBell";
import { fileUrl } from "@/lib/api";

export default function Header({ onSearch, searchValue }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [q, setQ] = useState(searchValue || "");

  const submit = (e) => {
    e.preventDefault();
    if (onSearch) onSearch(q);
    else nav(`/?search=${encodeURIComponent(q)}`);
  };

  return (
    <header className="cp-header sticky top-0 z-40" data-testid="app-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-4">
        <button
          onClick={() => window.dispatchEvent(new CustomEvent("cp-toggle-sidebar"))}
          className="lg:hidden p-2 rounded-full hover:bg-[#F9EFE6] transition -ml-1"
          aria-label="Open menu"
          data-testid="mobile-menu-btn"
        >
          <Menu className="w-5 h-5 text-[#3B2C24]" />
        </button>

        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="home-link">
          <img
            src="https://customer-assets.emergentagent.com/job_pop-talk/artifacts/rqc7hvyg_cpt%20logo.jpg"
            alt="Cake Pop Talk logo"
            className="w-10 h-10 rounded-full object-cover shadow-md"
            data-testid="header-logo"
          />
          <div className="hidden sm:block">
            <div className="font-heading font-bold text-xl text-[#3B2C24] leading-none">Cake Pop Talk</div>
            <div className="text-[10px] uppercase tracking-widest text-[#857163]">a forum for bakers</div>
          </div>
        </Link>

        <form onSubmit={submit} className="flex-1 max-w-xl mx-auto" data-testid="search-form">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#857163] pointer-events-none" />
            <input
              data-testid="search-input"
              className="cp-input !pl-11"
              placeholder="Search posts, tags, recipes…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
        </form>

        <div className="flex items-center gap-2">
          {user && user !== false && user.subscription_status === "trial" && user.subscription_days_left !== undefined && (
            <Link to="/upgrade" className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold hover:opacity-90 transition" style={{ background: "linear-gradient(135deg, rgba(244,168,59,0.20), rgba(255,126,103,0.20))", color: "#B26A00" }} data-testid="trial-banner">
              <Sparkles className="w-3 h-3" /> Trial: {user.subscription_days_left}d left
            </Link>
          )}
          {user && user !== false && user.subscription_status === "expired" && (
            <Link to="/upgrade" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold hover:opacity-90 transition text-white" style={{ background: "#C2185B" }} data-testid="trial-expired-banner">
              Trial expired — Subscribe
            </Link>
          )}
          {user && user !== false && <MessagesBell />}
          {user && user !== false && <NotificationBell />}
          {user && user !== false && (
            <Link to="/new" className="cp-btn-primary hidden sm:inline-flex items-center gap-1.5" data-testid="new-post-btn">
              <Plus className="w-4 h-4" />
              <span>New Post</span>
            </Link>
          )}

          {user && user !== false ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 hover:bg-[#F9EFE6] p-1 rounded-full transition" data-testid="user-menu-btn">
                  <Avatar className="w-9 h-9 border border-[#EBDCD1]">
                    <AvatarImage src={fileUrl(user.avatar_url)} />
                    <AvatarFallback className="bg-[#FFD07F] text-[#3B2C24] font-semibold">
                      {user.username?.slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-2xl border-[#EBDCD1]">
                <div className="px-3 py-2">
                  <div className="font-semibold text-sm">{user.username}</div>
                  <div className="text-xs text-[#857163]">{user.email}</div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav(`/u/${user.username}`)} data-testid="menu-profile">
                  <UserIcon className="w-4 h-4 mr-2" /> Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/new")} data-testid="menu-new-post">
                  <Plus className="w-4 h-4 mr-2" /> New Post
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/analyzer")} data-testid="menu-analyzer">
                  <Sparkles className="w-4 h-4 mr-2" style={{ color: "#F4A83B" }} /> AI Crumb Analyzer
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/scaler")} data-testid="menu-scaler">
                  <Sparkles className="w-4 h-4 mr-2" style={{ color: "#8EAC50" }} /> Recipe Scaler
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/my-bakes")} data-testid="menu-my-bakes">
                  <UserIcon className="w-4 h-4 mr-2" /> My Bakes
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/find-a-baker")} data-testid="menu-find-a-baker">
                  <MapPin className="w-4 h-4 mr-2" style={{ color: "#FF7E67" }} /> Cake Pop Finder
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/find-a-baker/settings")} data-testid="menu-finder-settings">
                  <MapPin className="w-4 h-4 mr-2 opacity-50" /> My Finder Presence
                </DropdownMenuItem>
                {user.is_seller ? (
                  <DropdownMenuItem onClick={() => nav("/seller/dashboard")} data-testid="menu-seller-dashboard">
                    <Store className="w-4 h-4 mr-2" /> Seller Dashboard
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onClick={() => nav("/seller/apply")} data-testid="menu-become-seller">
                    <Store className="w-4 h-4 mr-2" /> Become a seller
                  </DropdownMenuItem>
                )}
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => nav("/admin")} data-testid="menu-admin">
                    <Shield className="w-4 h-4 mr-2" /> Admin
                  </DropdownMenuItem>
                )}
                {user.creator_dashboard_token && (
                  <DropdownMenuItem onClick={() => nav(`/creator-dashboard/${user.creator_dashboard_token}`)} data-testid="menu-creator-dashboard">
                    <Coins className="w-4 h-4 mr-2" style={{ color: "#F4A83B" }} /> Creator Dashboard
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link to="/login" className="cp-btn-ghost hidden sm:inline-flex items-center gap-1.5" data-testid="login-link">
                <LogIn className="w-4 h-4" /> Log in
              </Link>
              <Link to="/register" className="cp-btn-primary inline-flex items-center gap-1.5" data-testid="register-link">
                <Sparkles className="w-4 h-4" /> Join
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
